import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import JapanTalentMap from './JapanTalentMap';

const TalentMap = () => {
    const { t } = useTranslation();
    const [talents, setTalents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [viewMode, setViewMode] = useState('list'); // 'list', 'region', or 'map'
    const [selectedRegion, setSelectedRegion] = useState('all');
    const [selectedOrigin, setSelectedOrigin] = useState('all');

    const fetchMapData = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await apiService.getTalentMapData();
            setTalents(data);
        } catch (error) {
            console.error('获取地图数据失败:', error);
            setError(t('loadFailed') + ': ' + error.message);
        } finally {
            setIsLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchMapData();
    }, [fetchMapData]);

    // 从地址中提取都道府县信息
    const extractPrefecture = (address) => {
        if (!address) return t('unknown');

        const prefectures = [
            '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
            '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
            '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
            '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
            '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
            '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
            '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
        ];

        for (const prefecture of prefectures) {
            if (address.includes(prefecture)) {
                return prefecture;
            }
        }
        return t('unknown');
    };

    // 国家名称和分野的日语翻译函数
    const getJapaneseNationality = (nationality) => {
        const nationalityMap = {
            "中国": "中国",
            "韩国": "韓国",
            "韓国": "韓国",
            "缅甸": "ミャンマー",
            "ミャンマー": "ミャンマー",
            "印度尼西亚": "インドネシア",
            "インドネシア": "インドネシア",
            "越南": "ベトナム",
            "ベトナム": "ベトナム",
            "台湾": "台湾",
            "日本": "日本",
            "菲律宾": "フィリピン",
            "フィリピン": "フィリピン",
            "泰国": "タイ",
            "タイ": "タイ"
        };
        return nationalityMap[nationality] || nationality;
    };

    const getOriginCountry = (talent) => {
        const normalized = getJapaneseNationality((talent?.nationality || '').trim());
        return normalized || t('originUnset');
    };

    const getJapaneseField = (field) => {
        const fieldMap = {
            "外食": "外食",
            "飲食料品製造": "飲食料品製造",
            "溶接": "溶接",
            "介护": "介護",
            "他": "他"
        };
        return fieldMap[field] || field;
    };

    // 按都道府県分组人才（只按事业所地址）
    const groupedByPrefecture = talents.reduce((acc, talent) => {
        const workPrefecture = extractPrefecture(talent.workplaceAddress);

        if (workPrefecture !== t('unknown')) {
            if (!acc[workPrefecture]) {
                acc[workPrefecture] = [];
            }
            acc[workPrefecture].push({ ...talent, addressType: 'work' });
        }

        return acc;
    }, {});

    const prefectureKeys = Object.keys(groupedByPrefecture).sort();

    // 计算已分类和未分类的人才数量
    const mappedTalentsCount = prefectureKeys.reduce((sum, key) => sum + groupedByPrefecture[key].length, 0);
    const unmappedTalentsCount = talents.length - mappedTalentsCount;

    const filteredTalents = selectedRegion === 'all'
        ? talents
        : groupedByPrefecture[selectedRegion] || [];

    const originOptions = Array.from(new Set(talents.map(getOriginCountry))).sort((a, b) => a.localeCompare(b, 'ja'));

    const mapFilteredTalents = talents.filter((talent) => (
        selectedOrigin === 'all' || getOriginCountry(talent) === selectedOrigin
    ));

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-lg text-gray-600">{t('loading')}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="text-red-700">{error}</div>
                <button
                    onClick={fetchMapData}
                    className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    {t('retry')}
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* ヘッダー */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('talentMap')}</h2>
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex gap-2">
                        <button
                            onClick={() => setViewMode('list')}
                            className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'list'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            {t('listView')}
                        </button>
                        <button
                            onClick={() => setViewMode('region')}
                            className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'region'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            {t('regionView')}
                        </button>
                        <button
                            onClick={() => setViewMode('map')}
                            className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'map'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            {t('mapView')}
                        </button>
                    </div>

                    {viewMode === 'region' && (
                        <select
                            value={selectedRegion}
                            onChange={(e) => setSelectedRegion(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg"
                        >
                            <option value="all">{t('allRegions')}</option>
                            {prefectureKeys.map(prefecture => (
                                <option key={prefecture} value={prefecture}>
                                    {prefecture} ({groupedByPrefecture[prefecture].length}人)
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            {/* 統計情報 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-700">{t('totalTalents')}</h3>
                    <p className="text-3xl font-bold text-blue-600">{talents.length}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-700">{t('mappedTalents') || '分類済み'}</h3>
                    <p className="text-3xl font-bold text-green-600">{mappedTalentsCount}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-700">{t('unmappedTalents') || '未分類'}</h3>
                    <p className="text-3xl font-bold text-orange-500">{unmappedTalentsCount}</p>
                    {unmappedTalentsCount > 0 && (
                        <p className="text-xs text-gray-500 mt-1">{t('unmappedNote') || '事業所住所が未登録'}</p>
                    )}
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-700">{t('activeRegions')}</h3>
                    <p className="text-3xl font-bold text-purple-600">{prefectureKeys.length}</p>
                </div>
            </div>

            {/* 人才リスト/都道府県別表示/地図表示 */}
            {viewMode === 'map' ? (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="p-4 border-b">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">{t('talentDistributionMap')}</h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    {t('mapDescription')}
                                </p>
                            </div>
                            <div className="w-full md:w-64">
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {t('mapOriginFilterLabel')}
                                </label>
                                <select
                                    value={selectedOrigin}
                                    onChange={(e) => setSelectedOrigin(e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
                                >
                                    <option value="all">{t('mapOriginFilterAll')}</option>
                                    {originOptions.map((origin) => (
                                        <option key={origin} value={origin}>
                                            {origin}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    <JapanTalentMap talents={mapFilteredTalents} />
                </div>
            ) : viewMode === 'list' ? (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('name')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('residenceAddress')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('workplace')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('nationality')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('field')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredTalents.map((talent, index) => (
                                    <tr key={`${talent.id}-${index}`} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {talent.fullName}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            <div className="max-w-xs truncate" title={talent.address}>
                                                {talent.address || t('unregistered')}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            <div className="max-w-xs truncate" title={talent.workplaceAddress}>
                                                <div className="font-medium">{talent.workplaceName || t('unregistered')}</div>
                                                <div className="text-xs">{talent.workplaceAddress || ''}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {getJapaneseNationality(talent.nationality) || t('unregistered')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {getJapaneseField(talent.field) || t('unregistered')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {selectedRegion === 'all' ? (
                        // 全地域表示
                        prefectureKeys.map(prefecture => (
                            <div key={prefecture} className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                                    {prefecture} ({groupedByPrefecture[prefecture].length}人)
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {groupedByPrefecture[prefecture].map((talent, index) => (
                                        <div key={`${talent.id}-${index}`} className="border border-gray-200 rounded-lg p-4">
                                            <h4 className="font-medium text-gray-900">{talent.fullName}</h4>
                                            <p className="text-sm text-gray-600">{getJapaneseNationality(talent.nationality)}</p>
                                            <p className="text-sm text-gray-600">{getJapaneseField(talent.field)}</p>
                                            <div className="mt-2 text-xs text-gray-500">
                                                {t('workplace')}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    ) : (
                        // 特定地域表示
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">
                                {selectedRegion} ({filteredTalents.length}人)
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {filteredTalents.map((talent, index) => (
                                    <div key={`${talent.id}-${index}`} className="border border-gray-200 rounded-lg p-4">
                                        <h4 className="font-medium text-gray-900">{talent.fullName}</h4>
                                        <p className="text-sm text-gray-600">{getJapaneseNationality(talent.nationality)}</p>
                                        <p className="text-sm text-gray-600">{getJapaneseField(talent.field)}</p>
                                        <div className="mt-2 text-xs text-gray-500">
                                            {t('workplace')}
                                        </div>
                                        <div className="mt-1 text-xs text-gray-400">
                                            {talent.workplaceAddress}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {talents.length === 0 && (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <p className="text-gray-600">{t('noTalentsWithAddress')}</p>
                </div>
            )}
        </div>
    );
};

export default TalentMap;
